import {
	OrganizationSettingsDtoContainerStatus,
	OrganizationSettingsDtoPreferredDistanceUnit,
	OrganizationSettingsDtoShipmentCondition,
	OrganizationSettingsDtoTransportType,
	PhoneType,
} from '@uturn/api/v1';
import { z } from '@uturn/portal/zod';
import { phoneNumber } from '@uturn/portal/zod/schema';
import {
	formValidationMessage,
	getRequiredFields,
	RequiredField,
} from '@uturn/portal/zod/utils';

/** @see PhoneDetailsDto */
const phoneDetailsSchema = z.object({
	type: z.nativeEnum(PhoneType),
	description: z.string().optional(),
	phone: phoneNumber.bareSchema,
});

/** @see AddressDto */
const addressSchema = z.object({
	street: z.string().optional(),
	houseNumber: z.string().optional(),
	postalCode: z.string(),
	city: z.string().optional(),
	countryCode: z.string().min(2).max(2).describe(RequiredField),
	latitude: z.number().optional(),
	longitude: z.number().optional(),
});

/** @see BankDetailsDto */
const bankDetailsSchema = z.object({
	accountNumber: z.string().optional(),
	bic: z.string().optional(),
});

/** @see FinancialDetailsUpdateDto */
const financialDetailsUpdateSchema = z.object({
	address: addressSchema.optional(),
	bankDetails: bankDetailsSchema,
});

/** @see OrganizationSettingsDto */
const organizationSettingsSchema = z.object({
	invoiceRequired: z.boolean().optional(),
	shipmentCondition: z
		.nativeEnum(OrganizationSettingsDtoShipmentCondition)
		.optional(),
	transportType: z.nativeEnum(OrganizationSettingsDtoTransportType).optional(),
	fixedPrice: z.boolean().optional(),
	requiresVat: z.boolean().optional(),
	containerStatus: z
		.nativeEnum(OrganizationSettingsDtoContainerStatus)
		.optional(),
	shippingLineId: z.number().optional(),
	preferredDistanceUnit: z
		.nativeEnum(OrganizationSettingsDtoPreferredDistanceUnit)
		.optional(),
});

/** Minimum valid email length, e.g. `a@b.c`. */
export const EMAIL_MIN_LENGTH = 5;
/** Maximum email address length per RFC 5321. */
export const EMAIL_MAX_LENGTH = 254;

/** @see OrganizationUpdateDto["emails"]: Altered for useFieldArray use. */
const emailFieldSchema = z.object({
	email: z.string().email().max(EMAIL_MAX_LENGTH).or(z.literal('')),
});

/** @see OrganizationUpdateDto["phoneNumbers.n.phone"]: Added for useFieldArray use. */
const phoneFieldSchema = z.object({
	phone: z
		.union([z.string(), phoneNumber.bareSchema])
		.transform((value, ctx) => {
			if (value === '') {
				return '';
			}
			const result = phoneNumber.schema.safeParse(value);
			if (!result.success) {
				for (const issue of result.error.issues) {
					ctx.addIssue(issue);
				}
				return z.NEVER;
			}
			return result.data;
		}),
});

const emailsFieldArraySchema = emailFieldSchema.array();
const phonesFieldArraySchema = phoneFieldSchema.array();

export type EmailsFieldArray = z.infer<typeof emailsFieldArraySchema>;
export type PhonesFieldArray = z.infer<typeof phonesFieldArraySchema>;

/** @see OrganizationUpdateDto */
const baseProfileSchema = z.object({
	name: z.string().max(128).describe(RequiredField),
	website: z
		.string()
		.max(128)
		.superRefine((val, ctx) => {
			if (val === '') {
				return;
			}
			const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(val)
				? val
				: `https://${val}`;
			try {
				const { hostname } = new URL(candidate);
				if (!/\.[a-z]{2,}$/i.test(hostname)) {
					throw new Error('hostname missing TLD');
				}
			} catch {
				ctx.addIssue({
					code: z.ZodIssueCode.invalid_string,
					validation: 'url',
					message: 'Invalid url',
				});
			}
		})
		.optional(),
	phoneNumbers: phoneDetailsSchema.array(),
	emails: emailsFieldArraySchema,
	phonesPrimary: phonesFieldArraySchema,
	phonesSecondary: phonesFieldArraySchema,
	address: addressSchema.describe(RequiredField),
	financialDetails: financialDetailsUpdateSchema,
	settings: organizationSettingsSchema.optional(),
});

export const requiredFields = getRequiredFields(baseProfileSchema);

/** Backend database column length. */
export const MAX_JOINED_LENGTH = {
	emails: 255,
	phonesPrimary: 30,
	phonesSecondary: 50,
} as const;

/** Backend used list separator. */
export const BE_EMAILS_LIST_SEPARATOR = ', ';

/** Estimated maximum number of elements per respective FieldArray's. */
export const maxCount = {
	phonesPrimary: Math.floor(
		MAX_JOINED_LENGTH.phonesPrimary / phoneNumber.MAX_LENGTH,
	),
	phonesSecondary: Math.floor(
		MAX_JOINED_LENGTH.phonesSecondary / phoneNumber.MAX_LENGTH,
	),
};

/** @see OrganizationUpdateDto (with backend database checks). */
export const formProfileSchema = baseProfileSchema.superRefine((val, ctx) => {
	const emailsJoinedLength = val.emails
		.map((email) => email.email)
		.filter((email) => email !== '')
		.join(BE_EMAILS_LIST_SEPARATOR).length;
	if (emailsJoinedLength > MAX_JOINED_LENGTH.emails) {
		ctx.addIssue({
			...formValidationMessage('emails_too_many'),
			code: z.ZodIssueCode.custom,
			path: [`emails.${val.emails.length - 1}.email`],
		});
	}

	const phonesPrimaryJoinedLength = val.phonesPrimary
		.map((phone) => phone.phone)
		.filter(phoneNumber.isType)
		.map(phoneNumber.getValue)
		.join(phoneNumber.LIST_SEPARATOR).length;
	if (phonesPrimaryJoinedLength > MAX_JOINED_LENGTH.phonesPrimary) {
		ctx.addIssue({
			...formValidationMessage('phones_limit_exceeded', {
				max: MAX_JOINED_LENGTH.phonesPrimary,
			}),
			code: z.ZodIssueCode.custom,
			path: [`phonesPrimary.${val.phonesPrimary.length - 1}.phone`],
		});
	}

	const phonesSecondaryJoinedLength = val.phonesSecondary
		.map((phone) => phone.phone)
		.filter(phoneNumber.isType)
		.map(phoneNumber.getValue)
		.join(phoneNumber.LIST_SEPARATOR).length;
	if (phonesSecondaryJoinedLength > MAX_JOINED_LENGTH.phonesSecondary) {
		ctx.addIssue({
			...formValidationMessage('phones_limit_exceeded', {
				max: MAX_JOINED_LENGTH.phonesSecondary,
			}),
			code: z.ZodIssueCode.custom,
			path: [`phonesSecondary.${val.phonesSecondary.length - 1}.phone`],
		});
	}
});

export type TForm = z.infer<typeof formProfileSchema>;
